import { useEffect, useRef } from 'react'

const W = 320
const H = 200
const MID_Y = 90
const WAVE_POINTS = 120
const NOISE_COUNT = 36
const GATE_U = 0.5 // gate sits at the halfway point of the timeline

// Speech segments along the timeline (silence in the gaps), in normalized
// 0..1 timeline coordinates. Brackets are drawn over the ones past the gate.
const SEGMENTS = [
  [0.03, 0.14], [0.20, 0.32], [0.39, 0.49],
  [0.55, 0.66], [0.72, 0.82], [0.88, 0.98],
]
const RIGHT_SEGMENTS = SEGMENTS.filter(([a]) => a >= GATE_U)
const SOFT = 0.02

const smoothstep = (x, a, b) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}
const speechEnv = (u) => {
  let e = 0
  for (const [a, b] of SEGMENTS) {
    const v = smoothstep(u, a - SOFT, a + SOFT) * (1 - smoothstep(u, b - SOFT, b + SOFT))
    if (v > e) e = v
  }
  return e
}

// "Find the speech, drop the rest": a noisy signal enters on the left, a
// VAD + denoiser gate in the middle eats the noise, and green brackets mark
// the speech segments the pipeline kept on the clean right-hand side.
export function Scene05Noise2D() {
  const pathRef = useRef()
  const noiseRefs = useRef([])
  const noiseState = useRef(
    Array.from({ length: NOISE_COUNT }, () => ({
      x: Math.random() * GATE_U * 0.9,
      y: (Math.random() - 0.5) * 70,
    }))
  )
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      const t = (now - start) / 1000
      const delta = 1 / 60

      let d = ''
      for (let i = 0; i <= WAVE_POINTS; i++) {
        const u = i / WAVE_POINTS
        const env = speechEnv(u)
        const carrier = Math.sin(u * 34 - t * 6) * 0.6 + Math.sin(u * 71 - t * 4.2) * 0.3
        let y = env * carrier * 34
        if (u < GATE_U) {
          const noiseAmt = 22 * (1 - smoothstep(u, GATE_U - 0.35, GATE_U))
          y += (Math.random() - 0.5) * noiseAmt
        }
        const x = u * W
        d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${(MID_Y + y).toFixed(1)} `
      }
      if (pathRef.current) pathRef.current.setAttribute('d', d)

      noiseState.current.forEach((p, i) => {
        p.x += delta * 0.16
        if (p.x > GATE_U + 0.02) {
          p.x = -Math.random() * 0.05
          p.y = (Math.random() - 0.5) * 70
        }
        p.y += Math.sin(t * 3 + i) * delta * 12
        const fade = 1 - smoothstep(p.x, GATE_U - 0.32, GATE_U + 0.02)
        const el = noiseRefs.current[i]
        if (el) {
          el.setAttribute('cx', (p.x * W).toFixed(1))
          el.setAttribute('cy', (MID_Y + p.y).toFixed(1))
          el.setAttribute('opacity', Math.max(0, fade * 0.9).toFixed(2))
        }
      })

      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  const gateX = GATE_U * W

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      <line x1="0" y1={MID_Y} x2={W} y2={MID_Y} stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

      {/* background-noise particles, dissolving at the gate */}
      {noiseState.current.map((_, i) => (
        <circle key={i} ref={(el) => { noiseRefs.current[i] = el }} r="2.2" fill="#ff3b54" />
      ))}

      {/* incoming + cleaned waveform, one continuous line */}
      <path ref={pathRef} fill="none" stroke="#5ff0ff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />

      {/* VAD + denoiser gate */}
      <rect x={gateX - 9} y="10" width="18" height={H - 40} fill="#00ffaa" opacity="0.12" />
      <rect x={gateX - 2} y="10" width="4" height={H - 40} fill="#7dffd2" opacity="0.7" />

      {/* green VAD brackets over detected speech, right of the gate */}
      {RIGHT_SEGMENTS.map(([a, b], i) => (
        <path
          key={i}
          d={`M${a * W},${MID_Y - 46} L${a * W},${MID_Y - 54} L${b * W},${MID_Y - 54} L${b * W},${MID_Y - 46}`}
          fill="none" stroke="#39e08a" strokeWidth="2" opacity="0.85"
        />
      ))}

      <text x={gateX / 2} y={H - 12} fill="#5d7891" fontSize="10" fontFamily="monospace" textAnchor="middle" letterSpacing="0.08em">NOISY</text>
      <text x={gateX + (W - gateX) / 2} y={H - 12} fill="#5d7891" fontSize="10" fontFamily="monospace" textAnchor="middle" letterSpacing="0.08em">CLEAN + VAD</text>
    </svg>
  )
}
